import { api } from "./client";
import type { CreateStocktakeRecipe, StocktakeRecipe } from "./inventoryStocktakes";

export interface Lookup {
  id: number;
  name: string;
}

// ─── Lookups (org DB, read-only) ──────────────────────────────────────────
export interface Warehouse {
  id: number;
  name: string;
}

export interface Supplier {
  id: number;
  name: string;
  eik?: string | null;
  // ДДС номер — само доставчик с ДДС номер начислява ДДС (бруто ≠ нето);
  // ЕИК-ът не е критерий (фирма с ЕИК може да не е регистрирана по ДДС)
  vatNumber?: string | null;
}

export interface Product {
  id: number;
  name: string;
  unit?: string | null; // мерна единица на продукта (напр. „килограм")
  unitId?: number | null;
  // Брой десетични знаци за количества в тази единица (0 = цяло, напр. „бр";
  // 3 = кг/л). Идва от units_of_measure.decimal_places. null → по подразбиране 3.
  decimalPlaces?: number | null;
  packagingIds?: number[]; // разфасовки, закачени към продукта
  category?: string | null; // група/категория (warehouse_groups.name)
  // Родителите на групата (warehouse_groups.parent_id) — имената от корена
  // надолу, без самата група; [] за корен/без група. По тях „Целият списък"
  // рисува групите като дърво. Липсва при стар BE (тогава — плоско).
  categoryParents?: string[];
  minQuantity?: number | null; // минимална наличност
  wastePct?: number | null; // фира (% брак/разход)
  vatId?: number | null; // ДДС: FK към vat.id
  vatRate?: number | null; // ДДС ставка в проценти (20 = 20%)
  vatName?: string | null; // име на ставката (напр. „20%")
  trackBatch?: boolean; // следене на партида → партидата е задължителна при доставка
  trackExpiry?: boolean; // следене на срок → срокът е задължителен при доставка
  // Изтрит (меко) в организацията: остава в списъка САМО за имената/мерките в
  // историята — пикерите го махат през `pickableProducts`. Липсва при стар BE.
  deleted?: boolean;
}

export interface Packaging {
  id: number;
  name: string;
  // Колко базови единици съдържа разфасовката (pieces * amount_per_piece)
  quantity?: number | null;
  pieces?: number | null;
  amount_per_piece?: number | null;
  unit?: string | null; // име на мерната единица (напр. „килограм")
}

// ─── Delivery (този проект — primary DB) ──────────────────────────────────
// Един ред (продукт) от доставка.
export interface DeliveryLine {
  id: number;
  productId: number;
  productName: string;
  warehouseId: number; // склад на реда (доставката може да е разпределена)
  warehouseName: string | null;
  packagingId: number | null;
  packagingName: string | null;
  packagingsCount: number | null; // брой разфасовки
  batchNumber: string | null; // партиден номер
  expiryDate: string | null; // срок (ISO date)
  quantity: number; // количество (базови единици)
  unitPrice: number; // ед. цена (до 5 знака; изведена от сумата, ако е въведена сума)
  sum: number; // себестойност (нето) = въведената сума на реда или quantity * unitPrice
  sumNoVat: number; // снимка без ДДС
  sumWithVat: number; // снимка с ДДС
  note: string | null; // бележка
}

// Заглавна част на доставка + редовете ѝ.
export interface Delivery {
  id: number;
  date: string; // дата на доставка (ISO)
  invoiceNumber: string | null; // фактура №
  supplierId: number | null;
  supplierName: string | null;
  // null = документ без склад на заглавието (складът е по ред — фактура над
  // стокови); BE `DeliveryOut.warehouse_id` е Optional
  warehouseId: number | null;
  warehouseName: string | null;
  note: string | null; // бележка към доставката
  productsCount: number; // брой редове/продукти
  totalValue: number; // вътрешна (себестойностна) обща сума
  totalValueNoVat: number; // обща без ДДС (снимка)
  totalValueWithVat: number; // обща с ДДС (снимка)
  // Кой е извършил операцията (created_by; името от org users)
  createdByName: string | null;
  lines: DeliveryLine[];
  // ─── От BE с редизайна (липсват при стар BE — тогава всичко е „фактура",
  // без статус на плащане и без връзки) ───
  // Тип документ; null = заварен запис (показва се като фактура)
  documentType?: DeliveryDocumentType | null;
  // Стокова, обобщена от фактура → фактурата; фактура → обобщените стокови
  invoiceId?: number | null;
  linkedNoteIds?: number[];
  linkedNotesTotalNoVat?: number;
  linkedNotesTotalWithVat?: number;
  paymentStatus?: DeliveryPaymentStatus;
  paidAt?: string | null;
  paymentMethod?: DeliveryPaymentMethod | null;
  paymentReference?: string | null;
  paidByName?: string | null;
  // Стокова, приключена без фактура (няма да дойде) — излиза от „Стокови без
  // фактура"; null/липсва = чака фактура (или стар BE)
  closedWithoutInvoiceAt?: string | null;
  closedWithoutInvoiceByName?: string | null;
  closedWithoutInvoiceReason?: string | null;
  createdAt?: string | null;
  status?: "active" | "annulled";
  deletedAt?: string | null;
  deletedByName?: string | null;
  deleteReason?: string | null;
  // Брой промени по документа (BE `DocumentMetaOut.version`, 0 = непроменян) —
  // връща се като `baseVersion` при редакция; стар BE — липсва (без сверка)
  version?: number;
}

// Литералите са общи с BE (docs/cross-repo-contract.md)
export type DeliveryDocumentType = "invoice" | "note";
export type DeliveryPaymentStatus = "paid" | "unpaid" | "by_invoice" | "untracked";
export type DeliveryPaymentMethod = "cash" | "bank" | "card";

// ─── Payload за създаване (заприходяване) ─────────────────────────────────
export interface CreateDeliveryLine {
  productId: number;
  warehouseId: number; // склад на реда
  packagingId: number | null;
  packagingsCount: number | null; // брой разфасовки
  quantity: number; // количество в базови единици (авторитетно)
  unitPrice: number; // ед. цена (до 5 знака)
  // Сумата на реда от листа — BE я записва ТОЧНО и извежда цената от нея;
  // без нея сумата е к-во × цена (стар FE). Бруто се пази, ако е съвместимо.
  netSum?: number;
  grossSum?: number;
  batchNumber?: string | null;
  expiryDate?: string | null; // ISO date
  note?: string | null;
}

export interface CreateDeliveryPayload {
  warehouseId: number;
  date: string; // ISO
  invoiceNumber?: string | null;
  supplierId: number | null;
  note?: string | null;
  lines: CreateDeliveryLine[];
  // От BE с редизайна; стар BE ги игнорира
  documentType?: DeliveryDocumentType;
  linkedNoteIds?: number[];
  payment?: {
    paidAt: string;
    method: DeliveryPaymentMethod;
    reference: string | null;
  } | null;
  // Само при редакция: версията, която листът е отворил (`version` на документа).
  // Различна от текущата → 409 с `conflict` (useDocumentConflict)
  baseVersion?: number;
}

// ─── Складови настройки (per ресторант) ────────────────────────────────────
export interface InventorySettings {
  // Дали фирмата (ресторантът) е регистрирана по ДДС (по подразбиране true).
  vatRegistered: boolean;
  // Склад по подразбиране за модула (замества избора от sidebar-а). null → първият.
  defaultWarehouseId?: number | null;
}

export async function getInventorySettings(): Promise<InventorySettings> {
  const res = await api.get<InventorySettings>("/inventory/settings");
  return res.data;
}

export async function updateInventorySettings(
  payload: InventorySettings,
): Promise<InventorySettings> {
  const res = await api.put<InventorySettings>("/inventory/settings", payload);
  return res.data;
}

// ─── API calls ────────────────────────────────────────────────────────────
export async function getWarehouses(): Promise<Warehouse[]> {
  const res = await api.get<Warehouse[]>("/inventory/warehouses");
  return res.data;
}

export async function getSuppliers(): Promise<Supplier[]> {
  const res = await api.get<Supplier[]>("/inventory/suppliers");
  return res.data;
}

// Нов доставчик направо от документа „Доставка". Записът отива в ОРГ базата —
// същият справочник, който се редактира и от екрана „Доставчици" в org-а.
export interface SupplierInput {
  name: string;
  eik?: string | null;
  vatNumber?: string | null;
}

export async function createSupplier(
  payload: SupplierInput,
): Promise<Supplier> {
  const res = await api.post<Supplier>("/inventory/suppliers", payload);
  return res.data;
}

// ВСИЧКИ продукти, и меко изтритите (`deleted: true`): документите, справките и
// историята търсят имената им по id. Списъкът за избор е `pickableProducts(...)`.
// Стар BE не познава `includeDeleted` и връща каквото винаги.
export async function getProducts(): Promise<Product[]> {
  const res = await api.get<Product[]>("/inventory/products", {
    params: { includeDeleted: true },
  });
  return res.data;
}

// ─── Справочници и създаване за модала „Нов продукт" ──────────────────────
// Продуктите живеят в ОРГ базата; модалът в „Доставка" повтаря екрана
// „Продукти" на организацията и ползва същите справочници.
export interface Unit {
  id: number;
  name: string;
  decimalPlaces: number;
}

// Групите идват ПОДРЕДЕНИ от бекенда (sort_index, после име) — същият ред
// като падащото меню в org екрана "Продукти"; не ги пресортирай тук.
export interface WarehouseGroupLookup {
  id: number;
  name: string;
}

export interface VatRate {
  id: number;
  vatRate: number;
  isDefault: boolean;
}

export interface ProductInput {
  name: string;
  vatId: number; // ДДС е задължително (както в org-а)
  exportCode?: number | null;
  groupId?: number | null;
  unitId?: number | null;
  minQuantity?: number | null;
  wastePct?: number | null;
  trackBatch: boolean;
  trackExpiry: boolean;
  packagingIds: number[];
}

export async function getUnits(): Promise<Unit[]> {
  const res = await api.get<Unit[]>("/inventory/units");
  return res.data;
}

export async function getWarehouseGroups(): Promise<WarehouseGroupLookup[]> {
  const res = await api.get<WarehouseGroupLookup[]>(
    "/inventory/warehouse-groups",
  );
  return res.data;
}

export async function getVats(): Promise<VatRate[]> {
  const res = await api.get<VatRate[]>("/inventory/vats");
  return res.data;
}

export async function createProduct(payload: ProductInput): Promise<Product> {
  const res = await api.post<Product>("/inventory/products", payload);
  return res.data;
}

export async function getPackagings(): Promise<Packaging[]> {
  const res = await api.get<Packaging[]>("/inventory/packagings");
  return res.data;
}

// Списъкът с доставки може да е без склад → всички доставки от всички складове.
export interface DeliveriesListParams {
  warehouseId?: number | null;
  startDate?: string;
  endDate?: string;
  // Темплейт-селектор при нова доставка: последните `limit` доставки на доставчик
  supplierId?: number | null;
  limit?: number;
  // „Въведени днес": граници по МОМЕНТА НА ВЪВЕЖДАНЕ (`created_at`, включителни),
  // не по датата на документа — доставка с минала дата, въведена днес, е „днес"
  createdFrom?: string;
  createdTo?: string;
}

// Параметрите на периода — само подадените (стар BE пренебрегва непознатите)
const listPeriodParams = (params: DeliveriesListParams) => ({
  ...(params.startDate ? { startDate: params.startDate } : {}),
  ...(params.endDate ? { endDate: params.endDate } : {}),
  ...(params.createdFrom ? { createdFrom: params.createdFrom } : {}),
  ...(params.createdTo ? { createdTo: params.createdTo } : {}),
});

export async function getDeliveries(
  params: DeliveriesListParams,
): Promise<Delivery[]> {
  const res = await api.get<Delivery[]>("/inventory/deliveries", {
    params: {
      ...(params.warehouseId != null ? { warehouseId: params.warehouseId } : {}),
      ...(params.supplierId != null ? { supplierId: params.supplierId } : {}),
      ...(params.limit != null ? { limit: params.limit } : {}),
      ...listPeriodParams(params),
    },
  });
  return res.data;
}

export async function createDelivery(
  payload: CreateDeliveryPayload,
): Promise<Delivery> {
  const res = await api.post<Delivery>("/inventory/deliveries", payload);
  return res.data;
}

// ─── Наличности (Σ остатъци по партиди) ───────────────────────────────────
export interface StockBalance {
  productId: number;
  productName: string;
  quantity: number; // наличност (знакова — може да е отрицателна)
  unit: string | null;
  weightedAvgPrice?: number | null; // средно претеглена цена
  lastDeliveryPrice?: number | null; // последна доставна цена
  lastDeliveryDate?: string | null; // дата на последна доставка (за подредба при замяна)
  // Продуктът е изтрит в организацията (меко или физически — тогава името е
  // „#<id>"). Такъв ред идва и на нула, ако има история в склада (без `asOf`).
  productDeleted?: boolean;
}

export async function getStockBalances(
  warehouseId: number,
  asOf?: string | null, // ISO ден; без него → текущи наличности, с него → към края на деня
  // с него → към момента `asOf`, вкл. вече записаното в същия момент (= системното
  // к-во, което ще види записът на инвентаризация в този момент)
  exact = false,
  // РЕДАКЦИЯ: без този документ и замените му — както ги маха записът на BE.
  // Само заедно с `asOf` (без него BE го пренебрегва, затова не се праща)
  excludeMovementId?: number | null,
): Promise<StockBalance[]> {
  const res = await api.get<StockBalance[]>("/inventory/stock", {
    params: {
      warehouseId,
      ...(asOf ? { asOf } : {}),
      ...(asOf && exact ? { asOfExact: true } : {}),
      ...(asOf && excludeMovementId != null ? { excludeMovementId } : {}),
    },
  });
  return res.data;
}

// ─── Хронология на движенията за продукт (натрупване на наличността) ───────
export interface StockHistoryEntry {
  movementId: number;
  date: string;
  type:
    | "delivery"
    | "writeoff"
    | "scrap"
    | "stocktake"
    | "substitution"
    | "transfer"
    | "report";
  quantityDelta: number; // знаков (+приход / −разход; инвентаризация = разликата)
  runningBalance: number; // наличност след реда
  unit: string | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  unitPrice: number; // себестойност на реда — винаги нето
}

// ─── Последни ед. цени по продукт (за prefill при доставка) ────────────────
export interface LastPrice {
  productId: number;
  unitPrice: number;
  quantity?: number | null; // доставено количество при последната доставка
  date?: string | null; // дата на последната доставка (ISO)
}

// С подаден supplierId цените са само от доставките на този доставчик —
// различните доставчици имат различни цени и чужда цена не е ориентир.
export async function getLastPrices(
  warehouseId: number,
  supplierId?: number | null,
): Promise<LastPrice[]> {
  const res = await api.get<LastPrice[]>("/inventory/last-prices", {
    params: { warehouseId, ...(supplierId != null ? { supplierId } : {}) },
  });
  return res.data;
}

// ─── Рецепти + съставки (за изписване на рецепта) ──────────────────────────
export interface RecipeIngredient {
  productId: number;
  productName: string;
  quantity: number;
  unit: string | null;
}

export async function getRecipes(): Promise<Lookup[]> {
  const res = await api.get<Lookup[]>("/inventory/recipes");
  return res.data;
}

export async function getRecipeIngredients(
  recipeId: number,
): Promise<RecipeIngredient[]> {
  const res = await api.get<RecipeIngredient[]>("/inventory/recipe-ingredients", {
    params: { recipeId },
  });
  return res.data;
}

// ─── Партиди (активни, FIFO подредени) ─────────────────────────────────────
export interface Lot {
  id: number;
  batchNumber: string | null;
  expiryDate: string | null;
  remaining: number;
  unitPrice: number;
  date: string;
}

export async function getLots(
  warehouseId: number,
  productId: number,
): Promise<Lot[]> {
  const res = await api.get<Lot[]>("/inventory/lots", {
    params: { warehouseId, productId },
  });
  return res.data;
}

// ─── Наличност + партиди КЪМ дата (за изписване със задна дата) ─────────────
export interface StockAsOf {
  productId: number;
  quantity: number; // знакова наличност към края на избрания ден
  unit: string | null;
  lots: Lot[]; // отворените партиди към датата (FIFO), с цената им тогава
}

export async function getStockAsOf(
  warehouseId: number,
  productId: number,
  asOf: string, // ISO дата/час; BE брои до края на този календарен ден
  // При РЕДАКЦИЯ на изписване — наличността БЕЗ собственото движение, както я
  // сверява записът на BE (иначе вече изписаното изглежда „над наличното")
  excludeMovementId?: number | null,
): Promise<StockAsOf> {
  const res = await api.get<StockAsOf>("/inventory/stock/as-of", {
    params: {
      warehouseId,
      productId,
      asOf,
      ...(excludeMovementId != null ? { excludeMovementId } : {}),
    },
  });
  return res.data;
}

// ─── Недостиг (shortages) ──────────────────────────────────────────────────
export interface ShortageBreakdown {
  date: string; // дата на отчета-причинител
  movementId: number;
  reportId: number | null;
  quantity: number; // недостиг, натрупан от този отчет
}
export interface Shortage {
  warehouseId: number;
  warehouseName: string | null;
  productId: number;
  productName: string;
  unit: string | null;
  deficit: number; // общ непокрит недостиг (>0)
  weightedAvgPrice: number | null;
  lastDeliveryPrice: number | null;
  earliestDate: string | null; // най-ранен причинител — дефаулт дата при компенсиране
  breakdown: ShortageBreakdown[];
}

export async function getShortages(): Promise<Shortage[]> {
  // Недостиг за ВСИЧКИ складове (forward-only) + разбивка по отчети.
  const res = await api.get<Shortage[]>("/inventory/shortages");
  return res.data;
}

// ─── Изписвания (write-offs) ───────────────────────────────────────────────
export interface WriteoffLine {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  sum: number; // себестойност = quantity * unitPrice
  sumNoVat: number; // снимка без ДДС
  sumWithVat: number; // снимка с ДДС
  batchNumber: string | null;
  note: string | null;
  // Запълнени само за редове от изписване на рецепта
  recipeId?: number | null;
  recipeName?: string | null;
  recipeQuantity?: number | null;
  priceManual?: boolean | null; // за редакция: дали цената е ръчно фиксирана
}

export interface Writeoff {
  id: number;
  date: string;
  // Вид: "writeoff" (изписване) | "scrap" (бракуване) — обща страница/endpoint-и,
  // видът е само маркер за колоната „Тип" и филтрите.
  kind: "writeoff" | "scrap";
  reason: string | null;
  comment: string | null;
  warehouseId: number;
  warehouseName: string | null;
  productsCount: number;
  totalValue: number; // себестойност (Σ sum)
  totalValueNoVat: number; // Σ снимки без ДДС
  totalValueWithVat: number; // Σ снимки с ДДС
  // Кой е извършил операцията (created_by; името от org users)
  createdByName: string | null;
  lines: WriteoffLine[];
  // От BE с редизайна (липсват при стар BE): кога е въведен документът и
  // анулирането му. `status` липсва → документът е активен.
  createdAt?: string | null;
  status?: "active" | "annulled";
  deletedAt?: string | null;
  deletedByName?: string | null;
  deleteReason?: string | null;
  // Брой промени по документа (BE `DocumentMetaOut.version`, 0 = непроменян) —
  // връща се като `baseVersion` при редакция; стар BE — липсва (без сверка)
  version?: number;
}

export interface CreateWriteoffLine {
  kind?: "product" | "recipe";
  productId?: number | null;
  recipeId?: number | null;
  quantity: number; // за рецепта = порции (множител)
  mode: "fifo" | "lot";
  lotId?: number | null;
  unitPrice?: number | null; // ръчно въведена ед. цена (по подразбиране = цената на партидата)
  note?: string | null;
}

export interface CreateWriteoffPayload {
  warehouseId: number;
  date: string;
  kind: "writeoff" | "scrap";
  reason?: string | null;
  comment?: string | null;
  lines: CreateWriteoffLine[];
  // Само при редакция: версията, която листът е отворил (`version` на документа).
  // Различна от текущата → 409 с `conflict` (useDocumentConflict)
  baseVersion?: number;
}

export async function getWriteoffs(
  params: DeliveriesListParams,
): Promise<Writeoff[]> {
  // Без warehouseId → всички изписвания от всички складове (както при доставките).
  const res = await api.get<Writeoff[]>("/inventory/writeoffs", {
    params: {
      ...(params.warehouseId != null ? { warehouseId: params.warehouseId } : {}),
      ...listPeriodParams(params),
    },
  });
  return res.data;
}

// Отказът за наличност при запис (изписване, прехвърляне): ЕДИН 400, чийто
// `detail` започва с този префикс и изброява ВСИЧКИ недостигащи продукти с
// нужното и наличното. Кросрепо литерал — BE `STOCK_SHORTAGE_PREFIX`
// (`app/features/inventory/stock_sufficiency.py`).
export const STOCK_SHORTAGE_PREFIX = "Недостатъчна наличност";

export const isStockShortageError = (error: unknown): boolean => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail.startsWith(STOCK_SHORTAGE_PREFIX);
};

export async function createWriteoff(
  payload: CreateWriteoffPayload,
): Promise<Writeoff> {
  const res = await api.post<Writeoff>("/inventory/writeoffs", payload);
  return res.data;
}

// ─── Редакция / триене (replay в BE) ───────────────────────────────────────
export async function getDelivery(id: number): Promise<Delivery> {
  const res = await api.get<Delivery>(`/inventory/deliveries/${id}`);
  return res.data;
}
export async function updateDelivery(
  id: number,
  payload: CreateDeliveryPayload,
): Promise<Delivery> {
  const res = await api.put<Delivery>(`/inventory/deliveries/${id}`, payload);
  return res.data;
}
export async function deleteDelivery(id: number): Promise<void> {
  await api.delete(`/inventory/deliveries/${id}`);
}

export async function getWriteoff(id: number): Promise<Writeoff> {
  const res = await api.get<Writeoff>(`/inventory/writeoffs/${id}`);
  return res.data;
}
export async function updateWriteoff(
  id: number,
  payload: CreateWriteoffPayload,
): Promise<Writeoff> {
  const res = await api.put<Writeoff>(`/inventory/writeoffs/${id}`, payload);
  return res.data;
}

// ─── Прехвърляне между складове (transfer) ──────────────────────────────────
export interface TransferLine {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number; // FIFO себестойност на източника
  sum: number;
  sumNoVat: number; // снимка без ДДС
  sumWithVat: number; // снимка с ДДС
}

export interface Transfer {
  id: number;
  date: string;
  fromWarehouseId: number;
  fromWarehouseName: string | null;
  toWarehouseId: number;
  toWarehouseName: string | null;
  note: string | null;
  productsCount: number;
  totalValue: number;
  totalValueNoVat: number;
  totalValueWithVat: number;
  // Кой е извършил операцията (created_by; името от org users)
  createdByName: string | null;
  lines: TransferLine[];
  // Брой промени по документа (BE `DocumentMetaOut.version`, 0 = непроменян) —
  // връща се като `baseVersion` при редакция; стар BE — липсва (без сверка)
  version?: number;
}

export interface CreateTransferLine {
  productId: number;
  quantity: number;
  mode: "fifo" | "lot";
  lotId?: number | null;
}

export interface CreateTransferPayload {
  fromWarehouseId: number;
  toWarehouseId: number;
  date: string;
  note?: string | null;
  lines: CreateTransferLine[];
  // Само при редакция: версията, която листът е отворил (`version` на документа).
  // Различна от текущата → 409 с `conflict` (useDocumentConflict)
  baseVersion?: number;
}

export async function createTransfer(
  payload: CreateTransferPayload,
): Promise<Transfer> {
  const res = await api.post<Transfer>("/inventory/transfers", payload);
  return res.data;
}
export async function updateTransfer(
  id: number,
  payload: CreateTransferPayload,
): Promise<Transfer> {
  const res = await api.put<Transfer>(`/inventory/transfers/${id}`, payload);
  return res.data;
}

// ─── Инвентаризации (stocktakes) ───────────────────────────────────────────
export interface StocktakeLine {
  id: number;
  productId: number;
  productName: string;
  kind: "surplus" | "shortage" | "substitute" | "equal";
  diff: number; // знаково: + излишък / − липса (0 = равно)
  unit?: string | null; // мерна единица (за разликата)
  unitPrice: number;
  sum: number; // знакова стойност (= с ДДС)
  sumNoVat: number; // знакова стойност без ДДС
  sumWithVat: number; // знакова стойност с ДДС
  systemQty?: number | null; // очаквана наличност (пази се на реда)
  counted?: number | null;
  priceManual?: boolean | null; // за префил при редакция
  batchNumber?: string | null; // само при излишък (новата партида)
  expiryDate?: string | null; // само при излишък (срок на новата партида)
  // частта от `counted` в приготвените рецепти; насипното = counted − inRecipes
  inRecipes?: number | null;
}

// Свързана замяна на стоктейк (за префил при редакция)
export interface StocktakeSubstitution {
  recipientProductId: number;
  donorProductId: number;
  donorQuantity: number;
  recipientQuantity: number;
}

export interface Stocktake {
  id: number;
  date: string;
  reason: string | null;
  warehouseId: number;
  warehouseName: string | null;
  surplusCount: number;
  shortageCount: number;
  totalValue: number; // нетна стойност (±) = с ДДС
  totalValueNoVat: number; // нетна стойност без ДДС
  totalValueWithVat: number; // нетна стойност с ДДС
  // Кой е извършил операцията (created_by; името от org users)
  createdByName: string | null;
  lines: StocktakeLine[];
  substitutions?: StocktakeSubstitution[]; // само при GET by id
  // приготвените рецепти (стар BE — липсва); съставките им са вече в `counted`
  recipes?: StocktakeRecipe[];
  // Брой промени по документа (BE `DocumentMetaOut.version`, 0 = непроменян) —
  // връща се като `baseVersion` при редакция; стар BE — липсва (без сверка)
  version?: number;
}

export interface CreateStocktakeLine {
  productId: number;
  counted: number;
  unitPrice?: number | null; // ръчна цена; null = последна (излишък) / FIFO (липса)
  batchNumber?: string | null; // само при излишък (новата партида)
  expiryDate?: string | null; // само при излишък (срок на новата партида)
}

// Замяна, изпълнена ПРЕДИ инвентаризацията (донорът се изписва; quantity е в
// единици на получателя).
export interface SubstitutionItem {
  recipientProductId: number;
  donorProductId: number;
  quantity: number;
}

export interface CreateStocktakePayload {
  warehouseId: number;
  date: string;
  reason?: string | null;
  lines: CreateStocktakeLine[];
  substitutions?: SubstitutionItem[];
  // приготвените рецепти — `lines[].counted` е тогава само НАСИПНОТО
  recipes?: CreateStocktakeRecipe[];
  // Само при редакция: версията, която листът е отворил (`version` на документа).
  // Различна от текущата → 409 с `conflict` (useDocumentConflict)
  baseVersion?: number;
}


export async function createStocktake(
  payload: CreateStocktakePayload,
): Promise<Stocktake> {
  const res = await api.post<Stocktake>("/inventory/stocktakes", payload);
  return res.data;
}
export async function getStocktake(id: number): Promise<Stocktake> {
  const res = await api.get<Stocktake>(`/inventory/stocktakes/${id}`);
  return res.data;
}

// ─── Взаимозаменяеми продукти (групи) ──────────────────────────────────────
// Група от взаимозаменяеми продукти (напр. кола / фанта / спрайт). Всеки член има
// коефициент към базова единица на групата (1 продукт = coefficient база) и
// sortIndex (приоритет при покриване; по-малко = по-рано).
export interface InterchangeMember {
  productId: number;
  productName: string;
  unit: string | null;
  coefficient: number;
  sortIndex: number | null;
}

export interface InterchangeGroup {
  id: number;
  warehouseId: number | null;
  hint: string | null;
  members: InterchangeMember[];
}

export interface InterchangeMemberPayload {
  productId: number;
  coefficient: number;
  sortIndex: number | null;
}

export interface InterchangeGroupPayload {
  hint: string | null;
  members: InterchangeMemberPayload[];
}

export async function getInterchangeGroups(): Promise<InterchangeGroup[]> {
  // Групите взаимозаменяеми са ОБЩИ за всички складове (без warehouseId).
  const res = await api.get<InterchangeGroup[]>("/inventory/interchange-groups");
  return res.data;
}
export async function createInterchangeGroup(
  payload: InterchangeGroupPayload,
): Promise<InterchangeGroup> {
  const res = await api.post<InterchangeGroup>("/inventory/interchange-groups", payload);
  return res.data;
}
export async function updateInterchangeGroup(
  id: number,
  payload: InterchangeGroupPayload,
): Promise<InterchangeGroup> {
  const res = await api.put<InterchangeGroup>(`/inventory/interchange-groups/${id}`, payload);
  return res.data;
}
export async function deleteInterchangeGroup(id: number): Promise<void> {
  await api.delete(`/inventory/interchange-groups/${id}`);
}

// ─── Замени (справка) ──────────────────────────────────────────────────────
export interface SubstitutionLine {
  id: number;
  donorProductId: number;
  donorProductName: string;
  donorUnit: string | null;
  recipientProductId: number;
  recipientProductName: string;
  recipientUnit: string | null;
  donorQuantity: number; // изписано от донора
  recipientQuantity: number; // покрито на получателя
  unitPrice: number;
  sum: number;
}

export interface Substitution {
  id: number;
  date: string;
  source: string; // тип/повод: "stocktake" (при инвентаризация)
  warehouseId: number;
  warehouseName: string | null;
  totalValue: number;
  // Кой е извършил операцията (created_by; името от org users)
  createdByName: string | null;
  lines: SubstitutionLine[];
}

export async function getSubstitutions(
  params: DeliveriesListParams,
): Promise<Substitution[]> {
  // Без warehouseId → всички замени от всички складове.
  const res = await api.get<Substitution[]>("/inventory/substitutions", {
    params: {
      ...(params.warehouseId != null ? { warehouseId: params.warehouseId } : {}),
      startDate: params.startDate,
      endDate: params.endDate,
    },
  });
  return res.data;
}
export async function updateStocktake(
  id: number,
  payload: CreateStocktakePayload,
): Promise<Stocktake> {
  const res = await api.put<Stocktake>(`/inventory/stocktakes/${id}`, payload);
  return res.data;
}

// ─── Разнасяне на отчети (report posting) ───────────────────────────────────
// Неразнесен отчет на заведението (за избор в страницата).
export interface PendingReport {
  id: number;
  reportNumber: number;
  reportDate: string;
  totalAmount: number;
  totalBillsCount: number;
  // Кога е затворен денят (Report.created_at) — от BE с редизайна; липсва при стар BE
  closedAt?: string | null;
  // Последното анулирано разнасяне на същия отчет („Върнат след анулиране на №X")
  annulledPostingId?: number | null;
}

// Ред от ЛЯВАТА колона — продаден продаваем × брой (isProduct = рецепта с 1 продукт).
export interface ReportPostingRecipe {
  recipeId: number;
  name: string;
  quantity: number;
  isProduct: boolean;
  // Продуктите, които изписва (същото разгъване като `products` на прегледа) —
  // по тях се реже „Изписване от склад" при филтъра и при отворен артикул.
  // Стар BE → липсва: изписването не се реже по филтъра, а отвореният артикул
  // чака съставките си (`recipeIngredientsQuery`)
  productIds?: number[];
  // Само в `GET /report-postings/{id}` на разнасяне със снимка: как е разгънат
  // ПРИ разнасянето (справката показва рецептата по нея). `null`/липсва —
  // разнасяне без снимка или стар BE → текущата рецепта; `[]` — не е изписван
  postedIngredients?: ReportPostingPostedIngredient[] | null;
}

// Съставка от снимката на разнасянето: бруто за ЕДНА порция в склада си
export interface ReportPostingPostedIngredient {
  productId: number;
  productName: string;
  unit: string | null;
  warehouseId: number | null;
  warehouseName: string | null;
  perPortion: number;
}

// Кандидат склад за изписване на продукт (наличност — само информативно).
export interface ReportPostingWarehouseOption {
  warehouseId: number;
  warehouseName: string | null;
  available: number;
}

// Откъде е предложен складът в прегледа на разнасянето (правилото е на BE,
// `warehouse_routing.py`, за ВСЯКА съставка): `recipe` — складът на съставката
// в рецептата (задава се в org портала), `station` — складът на станцията на
// рецептата; без нито едно — продуктът е без предизбор. „Склад по продукт" е
// махнат: непознат източник (стар BE праща и `product`) се показва като станцията.
export type DefaultWarehouseSource = "recipe" | "station";

// Предизбрана позиция на продукт в прегледа (`ReportPostingProduct.defaultAllocations`):
// BE ги праща с най-големия дял първи; празен списък = част от нуждата е без
// склад и продуктът стои без предизбор.
export interface ReportPostingDefaultAllocation {
  warehouseId: number;
  quantity: number;
  source: DefaultWarehouseSource;
}

// Ред от ДЯСНАТА колона — продукт за изписване + нужно к-во + кандидат складове.
export interface ReportPostingProduct {
  productId: number;
  productName: string;
  unit: string | null;
  requiredQuantity: number;
  defaultWarehouseId: number | null;
  // Предизборът по склад (BE от 01.10.2026; стар BE го няма → defaultWarehouseId).
  defaultAllocations?: ReportPostingDefaultAllocation[];
  warehouses: ReportPostingWarehouseOption[];
}

export interface ReportPostingSavedLine {
  productId: number;
  warehouseId: number;
  quantity: number;
  mode: "fifo" | "lot";
  lotId: number | null;
}

export interface ReportPostingSavedSubstitution {
  warehouseId: number;
  recipientProductId: number;
  donorProductId: number;
  quantity: number;
}

export interface ReportPostingPreview {
  reportId: number;
  reportNumber: number;
  reportDate: string;
  posted: boolean;
  movementId: number | null;
  date: string | null;
  recipes: ReportPostingRecipe[];
  products: ReportPostingProduct[];
  savedLines: ReportPostingSavedLine[];
  savedSubstitutions: ReportPostingSavedSubstitution[];
  // Ръчните поправки на служебния акаунт („За изписване" по продукт) — на това
  // разнасяне, а за неразнесен отчет — на последното му анулирано. Работният
  // лист тръгва от тях, не от продажбите (`useQuantityCorrections.ts`).
  // Стар BE → липсва = няма поправки.
  manualCorrections?: ReportPostingManualCorrection[];
}

export interface ReportPostingManualCorrection {
  productId: number;
  quantity: number;
}

export interface CreateReportPostingLine {
  productId: number;
  warehouseId: number;
  quantity: number;
  mode: "fifo" | "lot";
  lotId?: number | null;
  note?: string | null;
}

// Компенсиране на липса при разнасяне (донор изписан вместо получателя), per склад.
export interface ReportSubstitutionItem {
  warehouseId: number;
  recipientProductId: number;
  donorProductId: number;
  quantity: number; // в единици на получателя
}

export interface CreateReportPostingPayload {
  reportId: number;
  date: string;
  lines: CreateReportPostingLine[];
  substitutions?: ReportSubstitutionItem[];
}

export interface ReportPostingLine {
  id: number;
  productId: number;
  productName: string;
  warehouseId: number;
  warehouseName: string | null;
  quantity: number;
  unitPrice: number;
  sum: number;
  mode: "fifo" | "lot";
  lotId: number | null;
  // Количеството е ръчно коригирано от служебния акаунт (различно от
  // продажбите); стар BE → липсва = не е коригирано
  manuallyCorrected?: boolean;
}

export interface ReportPosting {
  id: number;
  reportId: number | null;
  reportNumber: number | null;
  date: string;
  productsCount: number;
  totalValue: number; // нето (себестойност)
  // Обща стойност по ДДС снимките на редовете (бруто = нето при нерегистрирана фирма)
  totalValueNoVat: number;
  totalValueWithVat: number;
  createdByName: string | null;
  // Продаденото в отчета: продаваеми с >1 съставка = рецепти, с 1 = продукти
  soldRecipesCount: number;
  soldProductsCount: number;
  substitutionsCount: number; // брой компенсирани липси (замени)
  lines: ReportPostingLine[];
  // От BE с редизайна (липсват при стар BE): кога е въведен документът и
  // анулирането му. `status` липсва → документът е активен.
  createdAt?: string | null;
  status?: "active" | "annulled";
  deletedAt?: string | null;
  deletedByName?: string | null;
  deleteReason?: string | null;
}

export interface ReportPostingsParams {
  startDate?: string;
  endDate?: string;
}

export async function getPendingReports(): Promise<PendingReport[]> {
  const res = await api.get<PendingReport[]>("/inventory/report-postings/pending");
  return res.data;
}

export async function getReportPostingPreview(
  reportId: number,
): Promise<ReportPostingPreview> {
  const res = await api.get<ReportPostingPreview>(
    "/inventory/report-postings/preview",
    { params: { reportId } },
  );
  return res.data;
}

export async function getReportPostings(
  params: ReportPostingsParams,
): Promise<ReportPosting[]> {
  const res = await api.get<ReportPosting[]>("/inventory/report-postings", {
    params,
  });
  return res.data;
}

// Зарежда разнесен отчет за редакция (двете колони + запазеното);
// `includeDeleted` — и анулирано разнасяне (за преглед в справката).
export async function getReportPosting(
  id: number,
  options: { includeDeleted?: boolean } = {},
): Promise<ReportPostingPreview> {
  const res = await api.get<ReportPostingPreview>(
    `/inventory/report-postings/${id}`,
    options.includeDeleted ? { params: { includeDeleted: true } } : undefined,
  );
  return res.data;
}

export async function createReportPosting(
  payload: CreateReportPostingPayload,
): Promise<ReportPosting> {
  const res = await api.post<ReportPosting>("/inventory/report-postings", payload);
  return res.data;
}



// ─── Справки: продадени продукти / рецепти ──────────────────────────────────
// Продадена рецепта, от която идва продукт в „Продадени продукти" — колко от
// изписаното на продукта (в неговата мярка) е от нея за периода.
export interface SoldSellableRecipe {
  sellableId: number;
  name: string;
  quantity: number;
}

// Листов продукт на рецепта по текущия ѝ състав (под-рецептите разгънати).
export interface SoldSellableIngredient {
  productId: number;
  name: string;
}

// Продаден продаваем за период: продукт (1 съставка) или рецепта (повече от 1).
export interface SoldSellable {
  sellableId: number;
  name: string;
  quantity: number; // рецепти: продадени бройки; продукти: изписано к-во (в мерната единица)
  avgPrice: number; // рецепти: реалната средна след отстъпките; продукти: себестойност/ед. по текущи цени
  totalSum: number; // с ДДС — рецепти: реално платеното (`total_sum`, след отстъпки); продукти: себестойност
  sumNoVat: number; // без ДДС — същото без ДДС
  ingredientsCount: number; // само за рецепти
  unit: string | null;
  unitQuantity?: number | null;
  // Само рецепти: последната продажба (ISO, затварянето на сметката) сред СЪЩИТЕ
  // редове, които броят количеството; продукти — null. Опционално: стар BE го няма.
  lastSoldAt?: string | null;
  // Търсачката по рецепта/продукт (`sold/soldSearchModel.ts`). Само продукти: от
  // кои продадени рецепти идва изписаното, най-голямото първо. Само рецепти:
  // листовите продукти на рецептата. Опционални: стар BE ги няма → търси по име.
  recipes?: SoldSellableRecipe[];
  ingredients?: SoldSellableIngredient[];
}

export interface SoldParams {
  startDate?: string;
  endDate?: string;
  // По избор: склад за себестойността на продуктите (Продадени продукти).
  warehouseId?: number;
}

export async function getSoldProducts(
  params: SoldParams,
): Promise<SoldSellable[]> {
  const res = await api.get<SoldSellable[]>("/inventory/sold-products", {
    params,
  });
  return res.data;
}

export async function getSoldRecipes(
  params: SoldParams,
): Promise<SoldSellable[]> {
  const res = await api.get<SoldSellable[]>("/inventory/sold-recipes", {
    params,
  });
  return res.data;
}

// ─── Справка „Изтрити дейности" (soft delete следа) ─────────────────────────
export interface DeletedMovementLine {
  productId: number;
  productName: string;
  warehouseId: number;
  warehouseName: string | null;
  quantity: number;
  unit: string | null;
  unitPrice: number;
  sumNoVat: number;
  sumWithVat: number;
  // Само за броени редове на инвентаризация (Търсено/Намерено/знакова разлика)
  counted: number | null;
  systemQty: number | null;
  diff: number | null;
}

export interface DeletedMovement {
  id: number;
  // delivery | writeoff | scrap | stocktake | transfer | substitution | report
  type: string;
  date: string; // датата на документа
  deletedAt: string;
  deletedBy: number | null;
  deletedByName: string | null;
  warehouseNames: string[];
  // Само за прехвърляне — от кой към кой склад е било
  fromWarehouseName: string | null;
  toWarehouseName: string | null;
  invoiceNumber: string | null;
  reason: string | null;
  note: string | null;
  productsCount: number;
  totalValueNoVat: number;
  totalValueWithVat: number;
  lines: DeletedMovementLine[];
}

export async function getDeletedMovements(
  params: DeliveriesListParams,
): Promise<DeletedMovement[]> {
  // Периодът е по ДАТАТА НА ИЗТРИВАНЕ (deletedAt), не по датата на документа.
  const res = await api.get<DeletedMovement[]>("/inventory/deleted", {
    params: { startDate: params.startDate, endDate: params.endDate },
  });
  return res.data;
}

// Възстановява изтрито движение — количествата се прилагат наново и записът се
// връща в нормалните списъци.
export async function restoreDeletedMovement(id: number): Promise<void> {
  await api.post(`/inventory/deleted/${id}/restore`);
}

// Разбивка по дни за един продаваем от „Продадени продукти/рецепти" (модалът
// при клик на ред). При продукти totalSum е null (справката е по себестойност).
export interface SoldDaily {
  date: string; // денят (ISO, 00:00 локално)
  quantity: number;
  totalSum: number | null;
}

export async function getSoldDaily(
  kind: "product" | "recipe",
  sellableId: number,
  params: SoldParams,
): Promise<SoldDaily[]> {
  const path =
    kind === "product"
      ? "/inventory/sold-products/daily"
      : "/inventory/sold-recipes/daily";
  const res = await api.get<SoldDaily[]>(path, {
    params: { sellableId, startDate: params.startDate, endDate: params.endDate },
  });
  return res.data;
}
